import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { ProfessorNote } from '@/types'
import {
  StickyNote,
  X,
  ChevronRight,
  ChevronLeft,
  Loader2,
  User,
  Calendar
} from 'lucide-react'

export default function AdminNotePanel() {
  const [searchParams, setSearchParams] = useSearchParams()
  const noteId = searchParams.get('note_id')
  const [note, setNote] = useState<ProfessorNote | null>(null)
  const [teacherName, setTeacherName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [minimized, setMinimized] = useState(false)

  useEffect(() => {
    if (noteId) {
      loadNote(noteId)
      setMinimized(false)
    } else {
      setNote(null)
    }
  }, [noteId])

  const loadNote = async (id: string) => {
    setIsLoading(true)
    try {
      const { data } = await (supabase
        .from('professor_notes') as any)
        .select('*, teacher:profiles!teacher_id(first_name, last_name)')
        .eq('id', id)
        .single()

      if (data) {
        setNote(data as ProfessorNote)
        setTeacherName(data.teacher ? `${data.teacher.first_name} ${data.teacher.last_name}` : 'Desconocido')
      }
    } catch (err) {
      console.error('Error loading note:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const closePanel = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('note_id')
    setSearchParams(newParams)
  }

  // No renderizar si no hay note_id
  if (!noteId) return null

  return (
    <div
      className={`fixed top-20 right-0 z-40 flex transition-all duration-300 ${minimized ? 'translate-x-[calc(100%-40px)]' : 'translate-x-0'}`}
      style={{ maxHeight: 'calc(100vh - 120px)' }}
    >
      {/* Botón de minimizar/expandir */}
      <button
        onClick={() => setMinimized(!minimized)}
        className="self-start mt-4 -mr-0 bg-amber-500 hover:bg-amber-600 text-white rounded-l-xl p-2 shadow-lg transition-colors"
        title={minimized ? 'Mostrar nota' : 'Minimizar'}
      >
        {minimized ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {/* Panel */}
      <div className="w-80 bg-white shadow-2xl border-l border-amber-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-900">Nota del profesor</span>
          </div>
          <button
            onClick={closePanel}
            className="p-1 hover:bg-amber-200 rounded text-amber-700"
            title="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
            </div>
          ) : note ? (
            <div className="space-y-4">
              {/* Label */}
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-mono text-sm font-semibold">
                  {note.location_label}
                </span>
              </div>

              {/* Nota */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{note.note}</p>
              </div>

              {/* Meta */}
              <div className="space-y-1.5 text-xs text-gray-500">
                <div className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  <span>{teacherName}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{new Date(note.created_at).toLocaleString('es-CL')}</span>
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs text-gray-400 italic">
                  Edita el contenido de esta página tomando en cuenta la nota del profesor.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-6">Nota no encontrada</p>
          )}
        </div>
      </div>
    </div>
  )
}
